import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { http } from '../../lib/api';
import { downloadAllCsv, MAX_EXPORT_ROWS } from '../../lib/csv';
import { AppLayout } from '../../components/layout';
import { Button, Card, MetricCard, StatusBadge } from '../../components/core';
import { DataTable, type Column, Pagination, SpinnerBlock } from '../../components/feedback';
import { invoiceColumn, InvoiceModal } from '../../components/invoice';
import { useUiStore } from '../../store/ui';
import { ADMIN_NAV } from './nav';
import type { AdminPaymentRowDto, AdminPaymentsPageDto } from '@livestreamx/shared-types';

const PAGE_SIZE = 25;
const METHODS = ['CASH', 'BANK_TRANSFER', 'UPI', 'CARD', 'CHEQUE', 'OTHER'];
const STATUSES = ['PENDING', 'PAID', 'FAILED', 'REFUNDED'];

const cur = (n: number, currency = 'INR') =>
  n.toLocaleString('en-IN', { style: 'currency', currency, minimumFractionDigits: 0, maximumFractionDigits: 2 });
const num = (n: number) => n.toLocaleString('en-IN');

/** ISO → `1 Sep 2026`; returns the raw value if it doesn't parse. */
function day(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

interface Filters {
  search: string;
  status: string;
  method: string;
  from: string;
  to: string;
}

const EMPTY: Filters = { search: '', status: '', method: '', from: '', to: '' };

/** `YYYY-MM-DD` picker values widened to whole days, so `to` includes its own date. */
function toQuery(f: Filters) {
  return {
    search: f.search || undefined,
    status: f.status || undefined,
    method: f.method || undefined,
    from: f.from ? `${f.from}T00:00:00` : undefined,
    to: f.to ? `${f.to}T23:59:59` : undefined,
  };
}

const CSV_HEADER = ['Date', 'Tenant', 'Plan', 'Cycle', 'Stream', 'Amount', 'Currency', 'Status', 'Method', 'Reference', 'Receipt', 'Provider', 'Note', 'Booked at'];
const toCsvRow = (r: AdminPaymentRowDto) => [
  r.paidAt, r.tenantName, r.planName, r.billingCycle, r.streamName ?? 'Stream not created', r.amount, r.currency,
  r.status, r.method, r.providerRef, r.receipt, r.provider, r.note, r.createdAt,
];

const columns: Column<AdminPaymentRowDto>[] = [
  {
    key: 'date',
    header: 'Date',
    render: (r) => (
      <div>
        <div className="font-medium">{day(r.paidAt)}</div>
        {/* A back-dated hand-collected payment is written after the money moved. */}
        {r.paidAt && day(r.paidAt) !== day(r.createdAt) && (
          <div className="text-muted text-xs">booked {day(r.createdAt)}</div>
        )}
      </div>
    ),
  },
  { key: 'tenant', header: 'Tenant', render: (r) => <span className="font-medium">{r.tenantName}</span> },
  {
    key: 'plan',
    header: 'Plan',
    render: (r) => (
      <span className="text-muted text-sm">
        {r.planName ?? '—'}{r.billingCycle ? ` · ${r.billingCycle.toLowerCase()}` : ''}
      </span>
    ),
  },
  {
    key: 'stream',
    header: 'Stream',
    render: (r) => (
      <span className="text-muted text-sm">{r.streamName ?? 'Stream not created'}</span>
    ),
  },
  { key: 'amount', header: 'Amount', render: (r) => <span className="font-medium">{cur(r.amount, r.currency)}</span> },
  { key: 'status', header: 'Status', render: (r) => <StatusBadge status={r.status} /> },
  { key: 'method', header: 'Method', render: (r) => <span className="text-muted text-sm">{r.method?.replace('_', ' ') ?? '—'}</span> },
  {
    key: 'ref',
    header: 'Reference',
    render: (r) => (
      <span className="text-muted text-sm">
        {r.providerRef ?? '—'}{r.receipt ? ` · ${r.receipt}` : ''}
      </span>
    ),
  },
  { key: 'note', header: 'Note', render: (r) => <span className="text-muted text-sm">{r.note ?? '—'}</span> },
];
export function AdminTransactionsPage() {
  const [data, setData] = useState<AdminPaymentsPageDto | null>(null);
  const [filters, setFilters] = useState<Filters>(EMPTY);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [invoice, setInvoice] = useState<AdminPaymentRowDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const toast = useUiStore((s) => s.toast);
  const navigate = useNavigate();

  // Debounced, so typing in the search box doesn't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      setFilters((f) => (f.search === search ? f : { ...f, search }));
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const set = useCallback(<K extends keyof Filters>(key: K, value: Filters[K]) => {
    setFilters((f) => ({ ...f, [key]: value }));
    setPage(1);
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    http
      .get<AdminPaymentsPageDto>('/api/admin/payments', { page, pageSize: PAGE_SIZE, ...toQuery(filters) })
      .then(setData)
      .catch((e) => setError((e as Error).message ?? 'Failed'))
      .finally(() => setLoading(false));
  }, [page, filters]);

  useEffect(() => {
    void load();
  }, [load]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const query = toQuery(filters);
      const { written, truncated } = await downloadAllCsv<AdminPaymentRowDto>({
        filename: 'transaction-history.csv',
        header: CSV_HEADER,
        fetchPage: (p) => http.get<AdminPaymentsPageDto>('/api/admin/payments', { page: p, pageSize: 100, ...query }),
        toRow: toCsvRow,
      });
      toast(
        truncated ? 'warning' : 'success',
        `Exported ${num(written)} transaction${written === 1 ? '' : 's'}`,
        truncated ? `Capped at ${num(MAX_EXPORT_ROWS)} rows — narrow the filters for a complete file.` : undefined,
      );
    } catch (e) {
      toast('danger', 'Export failed', (e as Error).message);
    }
    setExporting(false);
  };

  const filtered = !!(filters.search || filters.status || filters.method || filters.from || filters.to);
  const reset = () => {
    setSearch('');
    setFilters(EMPTY);
    setPage(1);
  };

  return (
    <AppLayout brand="LiveStreamX" navGroups={ADMIN_NAV} variant="admin">
      <div className="admin-page">
        <div className="page-header admin-page-header">
          <div>
            <h1 className="page-title">Transaction History</h1>
            <p className="page-subtitle">Every payment across all tenants — gateway and hand-collected</p>
          </div>
          <Button variant="secondary" onClick={exportCsv} disabled={exporting || !data?.total}>
            {exporting ? 'Exporting…' : '⭳ Download CSV'}
          </Button>
        </div>
        {error && <div className="alert danger">{error}</div>}

        {data && (
          <div className="stat-grid">
            <MetricCard label="Transactions" value={num(data.summary.count)} hint={filtered ? 'Matching filters' : 'All time'} />
            <MetricCard label="Collected" value={cur(data.summary.paidAmount)} tone="success" hint="Paid" />
            <MetricCard label="Pending" value={cur(data.summary.pendingAmount)} tone="warning" hint="Not yet settled" />
            <MetricCard label="Refunded" value={cur(data.summary.refundedAmount)} tone="info" />
            <MetricCard label="Failed" value={num(data.summary.failedCount)} tone="danger" />
          </div>
        )}

        <div className="toolbar">
          <div className="field grow">
            <input
              className="input"
              placeholder="Search order id, UTR, receipt, note…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="field" style={{ minWidth: 140 }}>
            <select className="input" value={filters.status} onChange={(e) => set('status', e.target.value)}>
              <option value="">All statuses</option>
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="field" style={{ minWidth: 160 }}>
            <select className="input" value={filters.method} onChange={(e) => set('method', e.target.value)}>
              <option value="">All methods</option>
              {METHODS.map((m) => <option key={m} value={m}>{m.replace('_', ' ')}</option>)}
            </select>
          </div>
          <div className="field">
            <label htmlFor="tx-from">From</label>
            <input id="tx-from" type="date" className="input" value={filters.from} onChange={(e) => set('from', e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="tx-to">To</label>
            <input id="tx-to" type="date" className="input" value={filters.to} onChange={(e) => set('to', e.target.value)} />
          </div>
          {filtered && <Button variant="secondary" onClick={reset}>Clear</Button>}
        </div>

        {loading && !data ? <SpinnerBlock /> : (
          <Card>
            <DataTable
              columns={[...columns, invoiceColumn<AdminPaymentRowDto>(setInvoice)]}
              rows={data?.items ?? []}
              onRowClick={(r) => navigate(`/admin/tenants/${r.tenantId}`)}
            />
            {!data?.items.length && (
              <p className="text-muted text-sm" style={{ margin: 0 }}>
                {filtered ? 'No transactions match these filters.' : 'No payments recorded yet.'}
              </p>
            )}
            <Pagination page={page} totalPages={data?.totalPages ?? 1} onPage={setPage} />
          </Card>
        )}

        {invoice && (
          <InvoiceModal r={invoice} billedTo={invoice.tenantName} onClose={() => setInvoice(null)} />
        )}
      </div>
    </AppLayout>
  );
}
